import { type Ref, ref } from 'vue'

import type { I18n } from '@backmarket/nuxt-module-i18n/types'

import type {
  GetReviewCollectionResponse,
  QuestionIdentifier,
  UploadedFileValue,
} from '~/api/reviews/types/GetReviewCollectionResponse'

import { buildQuestions } from '../helpers/buildQuestions'
import { buildReviewForm } from '../helpers/buildReviewForm'
import { buildReviewFormValidators } from '../helpers/buildValidators'
import type {
  FileValue,
  ReviewForm,
  ReviewFormQuestion,
} from '../models/review-form'

/**
 * Builds the form, validators and questions for the current step
 * based on the API data and current step index
 */
export function useBuildForm(
  data: Ref<GetReviewCollectionResponse | null>,
  formStep: Ref<number>,
  i18n: I18n,
) {
  const form = ref<ReviewForm>()
  const questions = ref<ReviewFormQuestion[]>([])
  const layout = ref<'default' | 'reviews-minimal'>('reviews-minimal')

  const isSummaryStep = () =>
    data.value ? formStep.value >= data.value.steps.length : false

  /**
   * This is necessary to maintain state when going back to a previous step
   */
  function saveValuesInForm(
    values: Record<
      QuestionIdentifier,
      string | number | UploadedFileValue[] | FileValue[]
    >,
    attachments?: UploadedFileValue[] | FileValue[],
  ) {
    form.value = {
      ...form.value,
      ...values,
      ...(attachments ? { review_attachments: attachments } : {}),
    }
  }

  const buildFormForCurrentStep = () => {
    if (!data.value) return
    if (isSummaryStep()) {
      layout.value = 'default'

      return
    }

    layout.value = 'reviews-minimal'

    questions.value = buildQuestions({
      apiData: data.value,
      stepIndex: formStep.value,
      i18n,
      productModel: data.value.product.model,
    })

    // NB: form cannot be a computed as the value of its fields will be updated by the input components
    form.value = buildReviewForm(
      questions.value,
      form.value,
      data.value.answers,
    )
  }

  const validators = buildReviewFormValidators({ questions, i18n })

  return {
    form,
    layout,
    questions,
    validators,
    isSummaryStep,
    saveValuesInForm,
    buildFormForCurrentStep,
  }
}
